import { useAppStateSelector, useAppStateStore } from '../../../../providers/AppStateProvider';
import { selectIsFormValid } from '../../../OMS/NewOrder/OrderSlice';
import { orderSliceToOrderRequestForPreTrade } from '../../../OMS/NewOrder/orderSliceToOrderRequest';
import type { PreTradeResponseData } from '../../redux/preTradeApi';
import { hasPreTradeRelevantChanges } from './hasPreTradeRelevantChanges';

/**
 * Compares current order form request payload with a pre-trade scenario's NewOrderSingle.
 * @returns true if any non-exempt order-affecting field differs from the pre-trade scenario's NOS.
 */
export function useOrderRequestChanged(scenario: PreTradeResponseData | undefined): boolean {
  const store = useAppStateStore();
  const isFormValid = useAppStateSelector(selectIsFormValid);
  // Subscribe to form changes so this comparison re-runs when order inputs change.
  useAppStateSelector(state => state.order.form);
  const strategiesByName = useAppStateSelector(state => state.referenceData.strategies.strategiesByName);

  if (!scenario) {
    return false;
  }

  // Fail closed if form is currently invalid.
  if (!isFormValid) {
    return true;
  }

  // Generate an order request based on current order form to compare against
  const orderRequest = orderSliceToOrderRequestForPreTrade({ appState: store.getState() });

  // Fail closed when we cannot build a request from current form state.
  if (!orderRequest) {
    return true;
  }

  const strategyName = scenario.NewOrderSingle?.Strategy ?? scenario.Strategy ?? orderRequest.Strategy;
  const strategy = strategyName ? strategiesByName.get(strategyName) : undefined;

  return hasPreTradeRelevantChanges(orderRequest, scenario.NewOrderSingle, strategy?.Parameters);
}
